// Port of SuiteModel/Maps.swift (what a map holds and how it's read) and SuiteHistory/ProjectDiff.swift's `MapChange`.
// A map (`FragmentKind` "map") is notes on a sheet of paper joined by lines, with smudged regions behind them and
// strokes of ink, kept in Research & Notes. Its `text` is unused: everything is under the root key `map`, a map of
// `notes`, `lines`, `regions` and `strokes` keyed by id, made with the document so two devices write into one set.
// The writes are `MapDocument` (mapDocument.ts); the store's side (the Mind Maps folder, a new map, locks) is StoreEditor's.
import type { InkColor } from '../contract.ts';
import { ROOT, type ObjID, type SwiftReader } from '../automerge/swiftDoc.ts';
import { stableUUID } from '../hashing.ts';
import { swiftCompare } from '../model/order.ts';
import { INK_COLORS } from '../proseMarkdown.ts';
import { trimCFWhitespace, trimCFWhitespaceAndNewlines } from '../unicode.ts';

/** `Maps` (DocumentStore+Maps.swift): where maps are kept. */
export const Maps = {
  folderTitle: 'Mind Maps',
  folderSynopsis: "The project's maps: notes joined by lines, for thinking before the binder has a shape.",
  /** Said when a map would leave Research & Notes (into the manuscript, Publishing or an alternate arrangement). */
  staysInResearch: 'Maps stay in Research & Notes.',
  /** Ids derived from the project, so two devices making their first map before they sync get one folder. */
  stableID: (projectID: string, purpose: string) => stableUUID(`${projectID}|maps|${purpose}`),
  folderID: (projectID: string) => Maps.stableID(projectID, 'folder'),
  folderNodeID: (projectID: string) => Maps.stableID(projectID, 'folder-node'),
  /** A new map's name: "Mind Map", else the first "Mind Map n" (from 2) no map has yet, compared ignoring case. */
  newTitle(taken: readonly string[]): string {
    const names = new Set(taken.map((t) => trimCFWhitespace(t).toLowerCase()));
    if (!names.has('mind map')) return 'Mind Map';
    let n = 2;
    while (names.has(`mind map ${n}`)) n += 1;
    return `Mind Map ${n}`;
  },
};

// MARK: What a map holds

/** A point on a map's paper, in canvas points (y downward). */
export interface MapPoint { x: number; y: number }

/** A note's outline, drawn in pencil. Unknown values read as none. */
export type MapOutline = 'none' | 'oval' | 'box' | 'cloud' | 'underline';
export const MAP_OUTLINES: readonly MapOutline[] = ['none', 'oval', 'box', 'cloud', 'underline'];

export type MapNoteSize = 'small' | 'normal' | 'large';
export const MAP_NOTE_SIZES: readonly MapNoteSize[] = ['small', 'normal', 'large'];

/** The face a note is set in: the book's serif (Newsreader), or the handwriting face. */
export type MapFace = 'serif' | 'hand';
export const MAP_FACES: readonly MapFace[] = ['serif', 'hand'];

/** What a note stands for in the project: a document (a live card) or a character, place, object or motif (a chip).
 *  Stored as `fragment:<id>` / `entity:<id>`, so Duplicate Project's id remapping reaches it. */
export type MapLink = { kind: 'fragment'; id: string } | { kind: 'entity'; id: string };

export const MapLink = {
  stored: (link: MapLink): string => `${link.kind}:${link.id}`,
  /** `MapLink(stored:)`: null for anything else. */
  parse(stored: string): MapLink | null {
    if (stored.startsWith('fragment:')) return { kind: 'fragment', id: stored.slice(9) };
    if (stored.startsWith('entity:')) return { kind: 'entity', id: stored.slice(7) };
    return null;
  },
  same: (a: MapLink | null, b: MapLink | null) => (a === null || b === null ? a === b : a.kind === b.kind && a.id === b.id),
};

export interface MapNote {
  id: string;
  text: string;
  at: MapPoint;
  /** Null = the note is as wide as its text, up to `MAP_AUTO_WIDTH`. */
  width: number | null;
  outline: MapOutline;
  /** Null = charcoal. */
  ink: InkColor | null;
  size: MapNoteSize;
  /** Set bold whole (Scapple's captions are). */
  bold: boolean;
  face: MapFace;
  link: MapLink | null;
  /** The note this one sits under in a stack, and its place there (`FractionalIndex`). */
  stack: string | null;
  stackOrder: string | null;
  createdAt: Date;
}

/** `MapNote.init`'s defaults: a new id (`IDs.make()`) and now, unless given. */
export function mapNote(o: Partial<MapNote> & { at: MapPoint }): MapNote {
  return {
    id: o.id ?? crypto.randomUUID().toLowerCase(), text: o.text ?? '', at: o.at, width: o.width ?? null, outline: o.outline ?? 'none',
    ink: o.ink ?? null, size: o.size ?? 'normal', bold: o.bold ?? false, face: o.face ?? 'serif', link: o.link ?? null,
    stack: o.stack ?? null, stackOrder: o.stackOrder ?? null, createdAt: o.createdAt ?? new Date(),
  };
}

/** A note's other properties, changed one at a time so concurrent changes to different ones merge. */
export type MapNoteProperty =
  | { outline: MapOutline }
  | { ink: InkColor | null }
  | { size: MapNoteSize }
  | { bold: boolean }
  | { face: MapFace }
  | { width: number | null }
  | { link: MapLink | null }
  | { stack: string | null; order: string | null };

/** `end` points at `to`. */
export type MapArrow = 'none' | 'end' | 'both';
export const MAP_ARROWS: readonly MapArrow[] = ['none', 'end', 'both'];

export type MapLineStyle = 'solid' | 'dotted';
export const MAP_LINE_STYLES: readonly MapLineStyle[] = ['solid', 'dotted'];

export interface MapLine {
  id: string;
  from: string;
  to: string;
  arrow: MapArrow;
  label: string;
  ink: InkColor | null;
  style: MapLineStyle;
}

export function mapLine(o: Partial<MapLine> & { from: string; to: string }): MapLine {
  return {
    id: o.id ?? crypto.randomUUID().toLowerCase(), from: o.from, to: o.to, arrow: o.arrow ?? 'none', label: o.label ?? '', ink: o.ink ?? null,
    style: o.style ?? 'solid',
  };
}

export type MapLineProperty = { arrow: MapArrow } | { ink: InkColor | null } | { style: MapLineStyle } | { label: string };

/** Whether the line joins these two notes, either way round. */
export const lineJoins = (line: MapLine, a: string, b: string) => (line.from === a && line.to === b) || (line.from === b && line.to === a);

/** A smudged wash behind notes, marking a part of the map. */
export interface MapRegion {
  id: string;
  at: MapPoint;
  width: number;
  height: number;
  ink: InkColor | null;
  label: string;
}

export function mapRegion(o: Partial<MapRegion> & { at: MapPoint; width: number; height: number }): MapRegion {
  return { id: o.id ?? crypto.randomUUID().toLowerCase(), at: o.at, width: o.width, height: o.height, ink: o.ink ?? null, label: o.label ?? '' };
}

/** A point of ink, with the pen's pressure there (1 when it had none). */
export interface MapStrokePoint { x: number; y: number; pressure: number }

/** Ink drawn with a Pencil, pen or mouse and not (yet) read as a note. */
export interface MapStroke {
  id: string;
  /** Where the pen went, as drawn. */
  points: MapStrokePoint[];
  ink: InkColor | null;
  width: number;
  /** How far the stroke has been moved since it was drawn (one value, so two moves never mix). */
  offset: MapPoint;
}

export function mapStroke(o: Partial<MapStroke> & { points: MapStrokePoint[] }): MapStroke {
  return { id: o.id ?? crypto.randomUUID().toLowerCase(), points: o.points, ink: o.ink ?? null, width: o.width ?? 2, offset: o.offset ?? { x: 0, y: 0 } };
}

/** `MapStroke.placed`: the points where the stroke is now (moved by its offset). */
export function placedStroke(stroke: MapStroke): MapStrokePoint[] {
  const { x, y } = stroke.offset;
  return x === 0 && y === 0 ? stroke.points : stroke.points.map((p) => ({ x: p.x + x, y: p.y + y, pressure: p.pressure }));
}

/** A map as read: its notes in the order made, the lines whose two notes are both there, regions and strokes. */
export interface MapContent {
  notes: MapNote[];
  lines: MapLine[];
  regions: MapRegion[];
  strokes: MapStroke[];
  /** Null when there's no centre, or its note is gone. */
  centre: string | null;
  /** The face new notes take. */
  face: MapFace;
}

export const emptyMapContent = (): MapContent => ({ notes: [], lines: [], regions: [], strokes: [], centre: null, face: 'serif' });

/** `MapContent.Key`. */
export const MapKey = { map: 'map', notes: 'notes', lines: 'lines', regions: 'regions', strokes: 'strokes', centre: 'centre', face: 'face' } as const;

/** The widest a note with no width of its own grows before its text wraps. */
export const MAP_AUTO_WIDTH = 240;

export const mapNoteOf = (content: MapContent, id: string) => content.notes.find((n) => n.id === id) ?? null;

/** The notes joined to `id` by a line, in the order the lines were read. */
export const mapNeighbours = (content: MapContent, id: string): string[] =>
  content.lines.flatMap((l) => (l.from === id ? [l.to] : l.to === id ? [l.from] : []));

/** Every note's words, line labels and region labels, one to a line: what search and the binder's filter read. */
export function mapPlainText(content: MapContent): string {
  return [...content.notes.map((n) => n.text), ...content.lines.map((l) => l.label), ...content.regions.map((r) => r.label)]
    .map(trimCFWhitespaceAndNewlines).filter((s) => s !== '').join('\n');
}

/** `Character.isNewline`'s characters ("\r\n" is one). */
const NEWLINES = new RegExp('\\r\\n|[\\n\\v\\f\\r' + String.fromCharCode(0x85, 0x2028, 0x2029) + ']');

/** The map as an indented list (MCP and the CLI; the Mac's mirror). The centre comes first with everything reached from
 *  it along the lines; then each group of notes not joined to it, from its top-left note; notes joined to a note already
 *  listed come under it, nearest the top first. A line's label follows the note it leads to, in parentheses. `title`
 *  names a linked note with no words of its own. */
export function mapOutline(content: MapContent, title: (link: MapLink) => string | null | undefined = () => null): string {
  if (!content.notes.length) return '';
  const words = (note: MapNote): string => {
    const text = note.text.split(NEWLINES).map(trimCFWhitespace).filter((s) => s !== '').join(' ');
    if (text !== '') return text;
    return (note.link ? title(note.link) : null) ?? '(empty note)';
  };
  const byID = new Map<string, MapNote>();
  for (const n of content.notes) if (!byID.has(n.id)) byID.set(n.id, n);
  const topLeft = (a: MapNote, b: MapNote) => a.at.y - b.at.y || a.at.x - b.at.x || swiftCompare(a.id, b.id);
  const listed = new Set<string>();
  const out: string[] = [];
  const list = (start: string) => {
    // Breadth first, so a note sits under the note nearest the start that reaches it.
    const children = new Map<string, [string, string][]>();
    const queue = [start];
    listed.add(start);
    for (let index = 0; index < queue.length; index++) {
      const id = queue[index];
      const found: [MapNote, string][] = [];
      for (const line of content.lines) {
        const other = line.from === id ? line.to : line.to === id ? line.from : null;
        const note = other === null ? undefined : byID.get(other);
        if (other === null || listed.has(other) || !note) continue;
        listed.add(other);
        found.push([note, trimCFWhitespaceAndNewlines(line.label)]);
      }
      found.sort((a, b) => topLeft(a[0], b[0]));
      children.set(id, found.map(([n, label]) => [n.id, label]));
      queue.push(...found.map(([n]) => n.id));
    }
    const write = (id: string, label: string, depth: number) => {
      const note = byID.get(id);
      if (!note) return;
      out.push('  '.repeat(depth) + '- ' + words(note) + (label === '' ? '' : ` (${label})`));
      for (const [child, childLabel] of children.get(id) ?? []) write(child, childLabel, depth + 1);
    };
    write(start, '', 0);
  };
  if (content.centre !== null && byID.has(content.centre)) list(content.centre);
  for (const note of [...content.notes].sort(topLeft)) if (!listed.has(note.id)) list(note.id);
  const regionLabels = content.regions.map((r) => trimCFWhitespaceAndNewlines(r.label)).filter((s) => s !== '');
  if (regionLabels.length) out.push('', ...regionLabels.map((l) => `Region: ${l}`));
  return out.join('\n');
}

/** `MapContent ==`: every note, line, region and stroke the same, in the same order, and the same centre and face. */
export function sameMapContent(a: MapContent, b: MapContent): boolean {
  const same = <T>(x: T[], y: T[], eq: (p: T, q: T) => boolean) => x.length === y.length && x.every((p, i) => eq(p, y[i]));
  const point = (p: MapPoint, q: MapPoint) => p.x === q.x && p.y === q.y;
  return a.centre === b.centre && a.face === b.face
    && same(a.notes, b.notes, (p, q) => p.id === q.id && p.text === q.text && point(p.at, q.at) && p.width === q.width && p.outline === q.outline
      && p.ink === q.ink && p.size === q.size && p.bold === q.bold && p.face === q.face && MapLink.same(p.link, q.link) && p.stack === q.stack
      && p.stackOrder === q.stackOrder && p.createdAt.getTime() === q.createdAt.getTime())
    && same(a.lines, b.lines, (p, q) => p.id === q.id && p.from === q.from && p.to === q.to && p.arrow === q.arrow && p.label === q.label
      && p.ink === q.ink && p.style === q.style)
    && same(a.regions, b.regions, (p, q) => p.id === q.id && point(p.at, q.at) && p.width === q.width && p.height === q.height && p.ink === q.ink
      && p.label === q.label)
    && same(a.strokes, b.strokes, (p, q) => p.id === q.id && p.ink === q.ink && p.width === q.width && point(p.offset, q.offset)
      && same(p.points, q.points, (s, t) => s.x === t.x && s.y === t.y && s.pressure === t.pressure));
}

// MARK: Numbers

/** How a map writes numbers: to a tenth of a point, without a trailing ".0", the same on every platform. */
export const MapNumber = {
  format(value: number): string {
    if (!Number.isFinite(value)) return '0';
    // Swift's `.toNearestOrAwayFromZero`.
    const tenths = Math.sign(value * 10) * Math.round(Math.abs(value * 10));
    if (tenths === 0) return '0';
    if (tenths % 10 === 0) return String(tenths / 10);
    return (tenths / 10).toFixed(1);
  },

  pair: (a: number, b: number) => MapNumber.format(a) + ' ' + MapNumber.format(b),

  /** The numbers in a stored "x y" (or "x y p"); a word that isn't one is passed by. */
  parse(text: string | null | undefined): number[] {
    return (text ?? '').split(' ').filter((w) => w !== '').flatMap((w) => {
      const n = swiftDouble(w);
      return n !== null && Number.isFinite(n) ? [n] : [];
    });
  },
};

/** `Double(String)` for decimals (with an optional exponent); "inf" and "nan" are numbers there, though not finite ones. */
function swiftDouble(text: string): number | null {
  if (/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(text)) return Number(text);
  if (/^[+-]?(inf|infinity|nan)$/i.test(text)) return NaN;
  return null;
}

// MARK: Reading

const oneOf = <T extends string>(all: readonly T[], raw: string | undefined): T | null =>
  raw !== undefined && (all as readonly string[]).includes(raw) ? (raw as T) : null;

/** Foundation's `Date.distantPast` (documents.ts's `DISTANT_PAST`, here without the import cycle). */
const DISTANT_PAST = -62135769600000;

/** `FragmentDocument.mapContent()`: what the map holds. Lines with a note missing (one deleted on another device) are
 *  left out, and repeats of a line joining the same two notes; a stack under a missing note is ignored. Throws as the
 *  Swift does on a value of the wrong type. */
export function readMapContent(r: SwiftReader): MapContent {
  const content = emptyMapContent();
  const root = r.map(ROOT, MapKey.map);
  if (root === undefined) return content;
  content.face = oneOf(MAP_FACES, r.string(root, MapKey.face)) ?? 'serif';
  const ink = (obj: ObjID) => oneOf(INK_COLORS, r.string(obj, 'ink'));
  const point = (text: string | undefined): MapPoint => {
    const at = MapNumber.parse(text);
    return { x: at[0] ?? 0, y: at.length > 1 ? at[1] : 0 };
  };

  const notes = r.map(root, MapKey.notes);
  if (notes !== undefined) {
    for (const [id, [t, o]] of r.entries(notes)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const link = r.string(obj, 'link');
      content.notes.push({
        id,
        text: r.string(obj, 'text') ?? '',
        at: point(r.string(obj, 'at')),
        width: r.double(obj, 'width') ?? null,
        outline: oneOf(MAP_OUTLINES, r.string(obj, 'outline')) ?? 'none',
        ink: ink(obj),
        size: oneOf(MAP_NOTE_SIZES, r.string(obj, 'size')) ?? 'normal',
        bold: r.bool(obj, 'bold') ?? false,
        face: oneOf(MAP_FACES, r.string(obj, 'face')) ?? 'serif',
        link: link === undefined ? null : MapLink.parse(link),
        stack: r.string(obj, 'stack') ?? null,
        stackOrder: r.string(obj, 'order') ?? null,
        createdAt: r.date(obj, 'createdAt') ?? new Date(DISTANT_PAST),
      });
    }
  }
  content.notes.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime() || swiftCompare(a.id, b.id));
  const ids = new Set(content.notes.map((n) => n.id));
  for (const note of content.notes) {
    if (note.stack !== null && (!ids.has(note.stack) || note.stack === note.id)) {
      note.stack = null;
      note.stackOrder = null;
    }
  }

  const lines = r.map(root, MapKey.lines);
  if (lines !== undefined) {
    const read: MapLine[] = [];
    for (const [id, [t, o]] of r.entries(lines)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const from = r.string(obj, 'from');
      if (from === undefined) continue;
      const to = r.string(obj, 'to');
      if (to === undefined || from === to || !ids.has(from) || !ids.has(to)) continue;
      read.push({
        id, from, to,
        arrow: oneOf(MAP_ARROWS, r.string(obj, 'arrow')) ?? 'none',
        label: r.string(obj, 'label') ?? '',
        ink: ink(obj),
        style: oneOf(MAP_LINE_STYLES, r.string(obj, 'style')) ?? 'solid',
      });
    }
    // Two devices joining the same notes before they sync make two lines: the first by id is the one shown.
    read.sort((a, b) => swiftCompare(a.id, b.id));
    for (const line of read) if (!content.lines.some((l) => lineJoins(l, line.from, line.to))) content.lines.push(line);
  }

  const regions = r.map(root, MapKey.regions);
  if (regions !== undefined) {
    for (const [id, [t, o]] of r.entries(regions)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const at = point(r.string(obj, 'at'));
      const size = MapNumber.parse(r.string(obj, 'size'));
      content.regions.push({
        id, at, width: Math.max(size[0] ?? 0, 1), height: Math.max(size.length > 1 ? size[1] : 0, 1), ink: ink(obj), label: r.string(obj, 'label') ?? '',
      });
    }
    content.regions.sort((a, b) => swiftCompare(a.id, b.id));
  }

  const strokes = r.map(root, MapKey.strokes);
  if (strokes !== undefined) {
    for (const [id, [t, o]] of r.entries(strokes)) {
      if (t !== 'map') continue;
      const obj = o as ObjID;
      const points: MapStrokePoint[] = [];
      const list = r.list(obj, 'points');
      if (list !== undefined) {
        for (const [pt, value] of r.values(list)) {
          if (pt !== 'str') continue;
          const p = MapNumber.parse(value as string);
          if (p.length < 2) continue;
          points.push({ x: p[0], y: p[1], pressure: p.length > 2 ? p[2] : 1 });
        }
      }
      if (!points.length) continue;
      content.strokes.push({ id, points, ink: ink(obj), width: r.double(obj, 'width') ?? 2, offset: point(r.string(obj, 'offset')) });
    }
    content.strokes.sort((a, b) => swiftCompare(a.id, b.id));
  }

  const centre = r.string(root, MapKey.centre);
  if (centre !== undefined && ids.has(centre)) content.centre = centre;
  return content;
}

// MARK: History (ProjectDiff.MapChange)

/** What changed on a map, counted. */
export interface MapChange {
  id: string;
  title: string;
  notesAdded: number;
  notesRemoved: number;
  notesMoved: number;
  notesReworded: number;
  linesAdded: number;
  linesRemoved: number;
  /** Restyled notes and lines, regions, strokes and the centre: changes with nothing to count. */
  otherChanges: boolean;
}

export const MapChange = {
  /** Null when the two are the same (a map missing on one side reads as an empty one). */
  compare(old: MapContent | null | undefined, next: MapContent | null | undefined, id: string, title: string): MapChange | null {
    const a = old ?? emptyMapContent(), b = next ?? emptyMapContent();
    if (sameMapContent(a, b)) return null;
    const change: MapChange = { id, title, notesAdded: 0, notesRemoved: 0, notesMoved: 0, notesReworded: 0, linesAdded: 0, linesRemoved: 0, otherChanges: false };
    const byID = (notes: MapNote[]) => { const m = new Map<string, MapNote>(); for (const n of notes) if (!m.has(n.id)) m.set(n.id, n); return m; };
    const before = byID(a.notes), after = byID(b.notes);
    change.notesAdded = [...after.keys()].filter((k) => !before.has(k)).length;
    change.notesRemoved = [...before.keys()].filter((k) => !after.has(k)).length;
    for (const [noteID, note] of after) {
      const was = before.get(noteID);
      if (!was) continue;
      if (was.at.x !== note.at.x || was.at.y !== note.at.y) change.notesMoved += 1;
      if (was.text !== note.text) change.notesReworded += 1;
    }
    const linesBefore = new Set(a.lines.map((l) => l.id)), linesAfter = new Set(b.lines.map((l) => l.id));
    change.linesAdded = [...linesAfter].filter((l) => !linesBefore.has(l)).length;
    change.linesRemoved = [...linesBefore].filter((l) => !linesAfter.has(l)).length;
    change.otherChanges = true;
    return change;
  },

  /** "3 notes added, 1 removed, 4 moved, 2 lines drawn": the counts that aren't zero. */
  summary(c: MapChange): string {
    const parts: string[] = [];
    const lines = (n: number) => `${n} ${n === 1 ? 'line' : 'lines'}`;
    if (c.notesAdded > 0) parts.push(`${c.notesAdded} ${c.notesAdded === 1 ? 'note' : 'notes'} added`);
    if (c.notesRemoved > 0) parts.push(`${c.notesRemoved} removed`);
    if (c.notesReworded > 0) parts.push(`${c.notesReworded} reworded`);
    if (c.notesMoved > 0) parts.push(`${c.notesMoved} moved`);
    if (c.linesAdded > 0 && c.linesRemoved > 0) parts.push(`${lines(c.linesAdded)} drawn, ${c.linesRemoved} rubbed out`);
    else if (c.linesAdded > 0) parts.push(`${lines(c.linesAdded)} drawn`);
    else if (c.linesRemoved > 0) parts.push(`${lines(c.linesRemoved)} rubbed out`);
    if (!parts.length && c.otherChanges) parts.push('restyled');
    return parts.join(', ');
  },
};
